import { useMemo } from 'react';

interface Props {
  html: string;
  className?: string;
  /** 'dark' = master dark preview (white text), 'light' = email-like preview (dark text) */
  theme?: 'dark' | 'light';
}

// Safe set of tags and attributes from our own Lexical editor
const ALLOWED_TAGS = new Set([
  'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike',
  'h2', 'h3', 'h4', 'blockquote',
  'ul', 'ol', 'li',
  'a', 'img',
  'span', 'div',
]);

const ALLOWED_ATTRS: Record<string, string[]> = {
  a: ['href', 'target', 'rel'],
  img: ['src', 'alt', 'width', 'height', 'style'],
  span: ['style'],
  div: ['style'],
};

/** Minimal client-side HTML sanitizer (no external deps) */
function sanitizeHtml(html: string): string {
  if (!html) return '';
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  const walk = (node: Element) => {
    const toRemove: Node[] = [];
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child as Element;
        const tag = el.tagName.toLowerCase();
        if (!ALLOWED_TAGS.has(tag)) {
          // Replace disallowed tag with its text content
          const text = document.createTextNode(el.textContent ?? '');
          node.insertBefore(text, el);
          toRemove.push(el);
        } else {
          // Strip disallowed attributes
          const allowedAttrs = ALLOWED_ATTRS[tag] ?? [];
          const attrsToRemove = Array.from(el.attributes)
            .map((a) => a.name)
            .filter((a) => !allowedAttrs.includes(a));
          attrsToRemove.forEach((a) => el.removeAttribute(a));

          // Force safe link attributes
          if (tag === 'a') {
            el.setAttribute('target', '_blank');
            el.setAttribute('rel', 'noopener noreferrer');
          }

          walk(el);
        }
      }
    });
    toRemove.forEach((el) => node.removeChild(el));
  };

  walk(doc.body);
  return doc.body.innerHTML;
}

const LIGHT_CSS = `
.rce-light p{margin-bottom:.5rem;font-size:14px;line-height:1.65;color:#374151}
.rce-light h2{font-size:18px;font-weight:700;margin-top:1rem;margin-bottom:.25rem;color:#111827}
.rce-light h3{font-size:15px;font-weight:700;margin-top:.75rem;margin-bottom:.25rem;color:#111827}
.rce-light blockquote{border-left:4px solid #7c3aed;padding-left:1rem;font-style:italic;color:#6b7280;margin:.75rem 0}
.rce-light ul{list-style-type:disc;padding-left:1.25rem;margin-bottom:.75rem}
.rce-light ol{list-style-type:decimal;padding-left:1.25rem;margin-bottom:.75rem}
.rce-light li{margin-bottom:.2rem;font-size:14px;color:#374151}
.rce-light a{color:#7c3aed;text-decoration:underline}
.rce-light strong,.rce-light b{font-weight:700}
.rce-light em,.rce-light i{font-style:italic}
.rce-light u{text-decoration:underline}
.rce-light img{max-width:100%;border-radius:8px;margin:8px 0;display:block}
`;

const DARK_CSS = `
.rce-dark p{margin-bottom:.5rem;font-size:14px;line-height:1.65;color:rgba(255,255,255,.7)}
.rce-dark h2{font-size:18px;font-weight:700;margin-top:1rem;margin-bottom:.25rem;color:#fff}
.rce-dark h3{font-size:15px;font-weight:700;margin-top:.75rem;margin-bottom:.25rem;color:#fff}
.rce-dark blockquote{border-left:4px solid rgba(167,139,250,.6);padding-left:1rem;font-style:italic;color:rgba(255,255,255,.4);margin:.75rem 0}
.rce-dark ul{list-style-type:disc;padding-left:1.25rem;margin-bottom:.75rem}
.rce-dark ol{list-style-type:decimal;padding-left:1.25rem;margin-bottom:.75rem}
.rce-dark li{margin-bottom:.2rem;font-size:14px;color:rgba(255,255,255,.7)}
.rce-dark a{color:#a78bfa;text-decoration:underline}
.rce-dark strong,.rce-dark b{font-weight:700;color:#fff}
.rce-dark em,.rce-dark i{font-style:italic}
.rce-dark u{text-decoration:underline}
.rce-dark img{max-width:100%;border-radius:8px;margin:8px 0;display:block}
`;

export const RichContentRenderer = ({ html, className = '', theme = 'light' }: Props) => {
  const clean = useMemo(() => sanitizeHtml(html ?? ''), [html]);
  const cls = theme === 'dark' ? 'rce-dark' : 'rce-light';
  const css = theme === 'dark' ? DARK_CSS : LIGHT_CSS;

  if (!clean) return null;

  return (
    <>
      <style>{css}</style>
      <div className={`${cls} ${className}`} dangerouslySetInnerHTML={{ __html: clean }} />
    </>
  );
};

/** Strips all HTML tags — used to extract plain text for notification body */
export function htmlToPlainText(html: string): string {
  if (!html) return '';
  const div = document.createElement('div');
  div.innerHTML = html.replace(/<[^>]*>/g, ' ');
  return (div.textContent ?? div.innerText ?? '').replace(/\s+/g, ' ').trim();
}
